import { apiSlice } from "../../../features/api/apiSlice";

/**
 * A Head of Department's own approval queue.
 *
 * Everything here is scoped by the server to the institutions whose recorded
 * HOD is the caller — the `hod` role gets you through the door, it does not
 * decide whose students you see.
 */

export type HodApplication = {
  id: number;
  form_no: string;
  name: string;
  surname: string;
  other_names: string;
  matric_no: string;
  programme: string;
  graduation: string;
  email: string;
  institution: string;
  submitted_at: string | null;
  status: "pending" | "approved" | "declined";
  decided_at: string | null;
  notes: string | null;
  decided_by_secretariat: boolean;
};

export const hodPledgeApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getHodApplications: b.query<
      { data: HodApplication[]; meta: any; pagination: any },
      { status?: string; q?: string; page?: number } | void
    >({
      query: (params) => ({
        url: "/hod/pledge-applications",
        params: Object.fromEntries(
          Object.entries(params ?? {}).filter(([, v]) => v !== "" && v != null),
        ),
      }),
      transformResponse: (r: any) => ({
        data: r?.data ?? [],
        meta: r?.meta ?? {},
        pagination: r?.pagination ?? null,
      }),
      providesTags: [{ type: "HodPledgeApplications" as const, id: "LIST" }],
    }),

    decideHodApplication: b.mutation<
      any,
      { id: number; status: "approved" | "declined"; notes?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/hod/pledge-applications/${id}/decision`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "HodPledgeApplications" as const, id: "LIST" }],
    }),

    /** Approval only — a decline needs its own reason, so it stays one at a time. */
    bulkApproveHodApplications: b.mutation<
      { message: string; data: { approved: number; skipped: number } },
      number[]
    >({
      query: (ids) => ({ url: "/hod/pledge-applications/bulk", method: "POST", body: { ids } }),
      invalidatesTags: [{ type: "HodPledgeApplications" as const, id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetHodApplicationsQuery,
  useDecideHodApplicationMutation,
  useBulkApproveHodApplicationsMutation,
} = hodPledgeApi;
